/* ===========================================================================
   ZYREX — theme.js
   ---------------------------------------------------------------------------
   The shared runtime. This file is deferred FIRST, before animations.js,
   cart.js, product.js, facets.js and predictive-search.js, because every one
   of them reaches for `window.ZUtils` and `window.ZToast` on its first line.
   Both globals are therefore published synchronously, at the top of this
   file, before a single custom element is defined.

   What lives here
   ---------------
   1. ZUtils    debounce, throttle, rafThrottle, formatMoney, fetchConfig,
                trapFocus, removeTrapFocus, getFocusable, lockScroll,
                unlockScroll, announce, onReady, prefersReducedMotion,
                copyToClipboard, sectionUrl, parseJSONScript, serializeForm,
                isTouch
   2. ZToast    a queued message stack rendered into <toast-stack>
   3. Custom elements
                <sticky-header> <nav-drawer> <details-disclosure>
                <accordion-group> <tabs-group> <slider-component>
                <quantity-input> <modal-dialog> <modal-opener>
                <share-button> <localization-form> <scroll-top>
                <countdown-timer> <toast-stack>
   4. Global enhancements
                header-aware anchor scrolling, external link hygiene,
                Theme Editor re-initialisation

   House rules obeyed throughout
   -----------------------------
   - A missing child never throws. Every element degrades to "does nothing".
   - No console output, ever. A storefront console must stay clean.
   - Every element cleans up in disconnectedCallback: listeners, observers,
     timers and rAF handles are all released.
   - Every definition is guarded, so a Theme Editor reload that re-evaluates
     this file cannot raise "this name has already been used".
   - prefers-reduced-motion is honoured in every animation path.
   =========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var win = window;
  var doc = document;
  var root = doc.documentElement;

  /* =======================================================================
     0 — Tiny internals
     Not exported. ZUtils below is the public surface.
     ======================================================================= */

  function isFn(value) {
    return typeof value === 'function';
  }

  function qs(selector, scope) {
    try {
      return (scope || doc).querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  function qsa(selector, scope) {
    try {
      return Array.prototype.slice.call((scope || doc).querySelectorAll(selector));
    } catch (error) {
      return [];
    }
  }

  function on(target, type, handler, options) {
    if (!target || !isFn(target.addEventListener)) return;
    target.addEventListener(type, handler, options);
  }

  function off(target, type, handler, options) {
    if (!target || !isFn(target.removeEventListener)) return;
    target.removeEventListener(type, handler, options);
  }

  function raf(fn) {
    return isFn(win.requestAnimationFrame) ? win.requestAnimationFrame(fn) : win.setTimeout(fn, 16);
  }

  function cancelRaf(id) {
    if (!id) return;
    if (isFn(win.cancelAnimationFrame)) win.cancelAnimationFrame(id);
    else win.clearTimeout(id);
  }

  function clamp(value, min, max) {
    if (!isFinite(value)) return min;
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isFinite(parsed) ? parsed : fallback;
  }

  function toFloat(value, fallback) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  /** Attribute truthiness: present-and-not-"false" counts as true. */
  function attrOn(el, name) {
    if (!el || !isFn(el.getAttribute)) return false;
    if (!el.hasAttribute(name)) return false;
    return el.getAttribute(name) !== 'false';
  }

  function focusSafely(node) {
    if (!node || !isFn(node.focus)) return;
    try {
      node.focus({ preventScroll: true });
    } catch (error) {
      try {
        node.focus();
      } catch (inner) {
        /* an element that refuses focus is not an error worth reporting */
      }
    }
  }

  function boot() {
    return win.Zyrex || {};
  }

  /** A translated UI string from the layout bootstrap, with an English fallback. */
  function string(group, key, fallback) {
    var strings = boot().strings || {};
    var bucket = strings[group];
    if (bucket && typeof bucket[key] === 'string' && bucket[key].length) return bucket[key];
    return fallback;
  }

  function setting(key, fallback) {
    var settings = boot().settings || {};
    return settings[key] === undefined ? fallback : settings[key];
  }

  function emit(target, name, detail) {
    if (!target || !isFn(target.dispatchEvent)) return;
    try {
      target.dispatchEvent(
        new CustomEvent(name, { bubbles: true, cancelable: false, detail: detail || {} })
      );
    } catch (error) {
      /* CustomEvent is unavailable — nothing in the theme depends on the event */
    }
  }

  /* =======================================================================
     1 — ZUtils
     ======================================================================= */

  var FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]),' +
    ' input:not([disabled]):not([type="hidden"]), select:not([disabled]),' +
    ' textarea:not([disabled]), iframe, object, embed, summary, audio[controls],' +
    ' video[controls], [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  /* ---- Focus trap stack -------------------------------------------------
     Several overlays can legitimately be layered (a modal opened from inside
     the cart drawer, for instance), so traps are a stack and only the top of
     the stack listens. removeTrapFocus() always pops the top. */
  var trapStack = [];
  var trapKeyHandlerAttached = false;

  function onTrapKeydown(event) {
    if (event.key !== 'Tab' && event.keyCode !== 9) return;
    var trap = trapStack[trapStack.length - 1];
    if (!trap || !trap.container || !doc.contains(trap.container)) return;

    var items = ZUtils.getFocusable(trap.container);
    if (!items.length) {
      event.preventDefault();
      focusSafely(trap.container);
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    var inside = trap.container.contains(active);

    if (event.shiftKey) {
      if (!inside || active === first) {
        event.preventDefault();
        focusSafely(last);
      }
      return;
    }

    if (!inside || active === last) {
      event.preventDefault();
      focusSafely(first);
    }
  }

  /* ---- Scroll lock ------------------------------------------------------
     Reference counted, because the cart drawer, the mobile menu and a modal
     may all be open at once in the Theme Editor. The scrollbar width is
     published as `--scrollbar-w`, which base.css pads `.no-scroll` with, so
     locking never shifts the layout sideways. If the page already reserves
     the gutter with `scrollbar-gutter: stable`, the measurement is skipped —
     padding on top of a reserved gutter is a double shift. */
  var scrollLockCount = 0;

  function gutterIsStable() {
    try {
      var value = win.getComputedStyle(root).scrollbarGutter;
      return typeof value === 'string' && value.indexOf('stable') !== -1;
    } catch (error) {
      return false;
    }
  }

  function measureScrollbar() {
    if (gutterIsStable()) return 0;
    var width = win.innerWidth - root.clientWidth;
    if (!isFinite(width) || width < 0) return 0;
    // A width above 40px is not a scrollbar, it is a mid-resize measurement.
    return width > 40 ? 0 : width;
  }

  /* ---- Live regions -----------------------------------------------------
     Created lazily, once, and reused. Clearing before writing is what makes a
     repeated message announce a second time. */
  var liveRegions = { polite: null, assertive: null };

  function liveRegion(priority) {
    var key = priority === 'assertive' ? 'assertive' : 'polite';
    if (liveRegions[key] && doc.body && doc.body.contains(liveRegions[key])) {
      return liveRegions[key];
    }
    if (!doc.body) return null;

    var region = doc.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('aria-live', key);
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('role', key === 'assertive' ? 'alert' : 'status');
    region.setAttribute('data-z-live', key);
    doc.body.appendChild(region);
    liveRegions[key] = region;
    return region;
  }

  /* ---- Money ------------------------------------------------------------
     The canonical Shopify money_format parser. Every placeholder Shopify can
     emit is handled, so a merchant on any locale sees the right separators. */
  function withDelimiters(cents, precision, thousands, decimal) {
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    var fixed = (amount / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var fraction = parts[1] ? decimal + parts[1] : '';
    return whole + fraction;
  }

  var ZUtils = {
    /* ---- Timing --------------------------------------------------------- */

    /**
     * Trailing-edge debounce. `immediate` fires on the leading edge instead.
     * The returned function exposes .cancel() so an element can drop a
     * pending call in disconnectedCallback.
     */
    debounce: function (fn, wait, immediate) {
      var timer = null;
      var delay = toInt(wait, 150);

      function debounced() {
        var context = this;
        var args = arguments;
        var callNow = immediate && timer === null;

        if (timer) win.clearTimeout(timer);
        timer = win.setTimeout(function () {
          timer = null;
          if (!immediate && isFn(fn)) fn.apply(context, args);
        }, delay);

        if (callNow && isFn(fn)) fn.apply(context, args);
      }

      debounced.cancel = function () {
        if (timer) win.clearTimeout(timer);
        timer = null;
      };

      return debounced;
    },

    /** Leading-edge throttle with a trailing call, for scroll and resize. */
    throttle: function (fn, wait) {
      var delay = toInt(wait, 100);
      var last = 0;
      var timer = null;
      var lastArgs = null;
      var lastThis = null;

      function invoke(time) {
        last = time;
        timer = null;
        if (isFn(fn)) fn.apply(lastThis, lastArgs || []);
        lastArgs = null;
        lastThis = null;
      }

      function throttled() {
        var time = Date.now();
        lastArgs = arguments;
        lastThis = this;
        var remaining = delay - (time - last);

        if (remaining <= 0) {
          if (timer) {
            win.clearTimeout(timer);
            timer = null;
          }
          invoke(time);
          return;
        }
        if (!timer) {
          timer = win.setTimeout(function () {
            invoke(Date.now());
          }, remaining);
        }
      }

      throttled.cancel = function () {
        if (timer) win.clearTimeout(timer);
        timer = null;
        lastArgs = null;
        lastThis = null;
      };

      return throttled;
    },

    /**
     * Coalesce calls into one animation frame. This is the right wrapper for
     * anything that reads layout or writes a transform on scroll.
     */
    rafThrottle: function (fn) {
      var handle = null;
      var lastArgs = null;
      var lastThis = null;

      function wrapped() {
        lastArgs = arguments;
        lastThis = this;
        if (handle) return;
        handle = raf(function () {
          handle = null;
          if (isFn(fn)) fn.apply(lastThis, lastArgs || []);
        });
      }

      wrapped.cancel = function () {
        cancelRaf(handle);
        handle = null;
      };

      return wrapped;
    },

    /* ---- Money ---------------------------------------------------------- */

    /**
     * Format an integer number of cents with a Shopify money format string.
     * Falls back to the shop's money_format from the layout bootstrap.
     */
    formatMoney: function (cents, format) {
      var value = typeof cents === 'string' ? cents.replace(/[^0-9\-.]/g, '').replace('.', '') : cents;
      var amount = Number(value);
      if (!isFinite(amount)) amount = 0;

      var pattern = format || setting('moneyFormat', '') || '${{amount}}';
      var placeholder = /\{\{\s*(\w+)\s*\}\}/;
      var match = String(pattern).match(placeholder);
      if (!match) return String(amount / 100);

      var formatted;
      switch (match[1]) {
        case 'amount':
          formatted = withDelimiters(amount, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          formatted = withDelimiters(amount, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          formatted = withDelimiters(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = withDelimiters(amount, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = withDelimiters(amount, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          formatted = withDelimiters(amount, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          formatted = withDelimiters(amount, 0, ' ', '.');
          break;
        case 'amount_with_period_and_space_separator':
          formatted = withDelimiters(amount, 2, ' ', '.');
          break;
        default:
          formatted = withDelimiters(amount, 2, ',', '.');
      }

      return String(pattern).replace(placeholder, formatted);
    },

    /* ---- Network -------------------------------------------------------- */

    /**
     * Request init for the Shopify AJAX APIs. `type` is what we ask for back:
     * 'json' for the cart routes, 'html' or 'text' for Section Rendering.
     */
    fetchConfig: function (type, options) {
      var accept = 'application/json';
      if (type === 'html' || type === 'text') accept = 'text/html';
      if (type === 'javascript') accept = 'application/javascript';

      var opts = options || {};
      var config = {
        method: opts.method || 'POST',
        headers: {
          'Content-Type': opts.contentType || 'application/json',
          Accept: accept,
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      if (opts.contentType === false) delete config.headers['Content-Type'];
      if (opts.body !== undefined) config.body = opts.body;
      if (opts.signal) config.signal = opts.signal;
      return config;
    },

    /**
     * Build a Section Rendering API url.
     *   sectionUrl('main-collection', { url: '/collections/all', params: { page: 2 } })
     */
    sectionUrl: function (sectionId, options) {
      var opts = options || {};
      var base = opts.url || win.location.pathname + win.location.search;
      var url;

      try {
        url = new URL(base, win.location.origin);
      } catch (error) {
        return base;
      }

      if (opts.clean) {
        var keep = url.searchParams.get('q');
        url.search = '';
        if (keep) url.searchParams.set('q', keep);
      }

      if (sectionId) url.searchParams.set('section_id', String(sectionId));

      var params = opts.params || {};
      Object.keys(params).forEach(function (key) {
        var value = params[key];
        if (value === null || value === undefined || value === '') url.searchParams.delete(key);
        else url.searchParams.set(key, String(value));
      });

      return url.pathname + (url.search ? url.search : '');
    },

    /* ---- Focus ---------------------------------------------------------- */

    /** Every tabbable descendant that is actually rendered and not inert. */
    getFocusable: function (container) {
      if (!container || !isFn(container.querySelectorAll)) return [];
      return qsa(FOCUSABLE_SELECTOR, container).filter(function (node) {
        if (node.hasAttribute('inert')) return false;
        if (node.closest && node.closest('[inert]')) return false;
        if (node.closest && node.closest('[hidden]')) return false;
        if (node.getAttribute('aria-hidden') === 'true') return false;
        if (node.tabIndex < 0) return false;
        if (node.offsetWidth === 0 && node.offsetHeight === 0 && !node.getClientRects().length) {
          return false;
        }
        return true;
      });
    },

    /**
     * Constrain Tab to `container`. Traps stack, so opening a modal from a
     * drawer and closing it again restores the drawer's trap.
     */
    trapFocus: function (container, initial) {
      if (!container) return null;

      var entry = {
        container: container,
        previous: doc.activeElement && doc.activeElement !== doc.body ? doc.activeElement : null
      };
      trapStack.push(entry);

      if (!trapKeyHandlerAttached) {
        on(doc, 'keydown', onTrapKeydown, true);
        trapKeyHandlerAttached = true;
      }

      var target = initial || ZUtils.getFocusable(container)[0] || container;
      if (target === container && !container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
        entry.tempTabindex = true;
      }
      focusSafely(target);
      return entry;
    },

    /** Pop the top trap and return focus to `returnTo` (or the opener). */
    removeTrapFocus: function (returnTo) {
      var entry = trapStack.pop() || null;

      if (!trapStack.length && trapKeyHandlerAttached) {
        off(doc, 'keydown', onTrapKeydown, true);
        trapKeyHandlerAttached = false;
      }

      if (entry && entry.tempTabindex && entry.container) {
        entry.container.removeAttribute('tabindex');
      }

      var target = returnTo || (entry ? entry.previous : null);
      if (target && doc.contains(target)) focusSafely(target);
      return entry;
    },

    /* ---- Scroll lock ---------------------------------------------------- */

    lockScroll: function () {
      scrollLockCount += 1;
      if (scrollLockCount > 1) return;
      root.style.setProperty('--scrollbar-w', measureScrollbar() + 'px');
      if (doc.body) doc.body.classList.add('no-scroll');
    },

    unlockScroll: function (force) {
      scrollLockCount = force ? 0 : Math.max(0, scrollLockCount - 1);
      if (scrollLockCount > 0) return;
      if (doc.body) doc.body.classList.remove('no-scroll');
      root.style.setProperty('--scrollbar-w', '0px');
    },

    /* ---- Announcements -------------------------------------------------- */

    /** Speak a message to assistive tech without changing the visible page. */
    announce: function (message, priority) {
      if (!message) return;
      var region = liveRegion(priority);
      if (!region) return;
      region.textContent = '';
      win.setTimeout(function () {
        region.textContent = String(message);
      }, 60);
    },

    /* ---- Environment ---------------------------------------------------- */

    onReady: function (fn) {
      if (!isFn(fn)) return;
      if (doc.readyState === 'loading') {
        on(doc, 'DOMContentLoaded', function handler() {
          off(doc, 'DOMContentLoaded', handler);
          try {
            fn();
          } catch (error) {
            /* one broken callback must not stop the rest of the theme */
          }
        });
        return;
      }
      win.setTimeout(function () {
        try {
          fn();
        } catch (error) {
          /* as above */
        }
      }, 0);
    },

    prefersReducedMotion: function () {
      if (root.classList.contains('no-anim')) return true;
      try {
        return win.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (error) {
        return false;
      }
    },

    isTouch: function () {
      try {
        if (win.matchMedia('(hover: none) and (pointer: coarse)').matches) return true;
      } catch (error) {
        /* fall through to the capability sniff */
      }
      return 'ontouchstart' in win || (win.navigator && win.navigator.maxTouchPoints > 0);
    },

    /* ---- Data ----------------------------------------------------------- */

    /**
     * Read a `<script type="application/json">`. Accepts the script itself, a
     * container to search inside, or a selector. Returns null, never throws.
     */
    parseJSONScript: function (target) {
      var node = target;
      if (typeof target === 'string') node = qs(target);
      if (!node) return null;

      var script =
        node.tagName === 'SCRIPT' ? node : qs('script[type="application/json"]', node);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    },

    /**
     * Form values as a plain object. Repeated names collect into an array,
     * which is what a checkbox filter group needs.
     */
    serializeForm: function (form) {
      var data = {};
      if (!form || !form.elements) return data;

      qsa('input, select, textarea', form).forEach(function (field) {
        var name = field.name;
        if (!name || field.disabled) return;
        var type = (field.type || '').toLowerCase();
        if (type === 'file' || type === 'submit' || type === 'button' || type === 'reset') return;
        if ((type === 'checkbox' || type === 'radio') && !field.checked) return;

        var value = field.value;
        if (field.tagName === 'SELECT' && field.multiple) {
          value = qsa('option', field)
            .filter(function (option) {
              return option.selected;
            })
            .map(function (option) {
              return option.value;
            });
        }

        if (Object.prototype.hasOwnProperty.call(data, name)) {
          if (!Array.isArray(data[name])) data[name] = [data[name]];
          data[name] = data[name].concat(value);
        } else {
          data[name] = value;
        }
      });

      return data;
    },

    /** Copy text to the clipboard. Resolves true on success, false otherwise. */
    copyToClipboard: function (text) {
      var value = String(text === undefined || text === null ? '' : text);

      if (win.navigator && win.navigator.clipboard && isFn(win.navigator.clipboard.writeText)) {
        try {
          return win.navigator.clipboard.writeText(value).then(
            function () {
              return true;
            },
            function () {
              return legacyCopy(value);
            }
          );
        } catch (error) {
          return Promise.resolve(legacyCopy(value));
        }
      }

      return Promise.resolve(legacyCopy(value));
    }
  };

  /** The pre-Clipboard-API path, kept for older Safari and http:// previews. */
  function legacyCopy(value) {
    if (!doc.body) return false;
    var field = doc.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', 'readonly');
    field.setAttribute('aria-hidden', 'true');
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    field.style.opacity = '0';
    doc.body.appendChild(field);

    var copied = false;
    try {
      field.select();
      field.setSelectionRange(0, value.length);
      copied = doc.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    if (field.parentNode) field.parentNode.removeChild(field);
    return copied;
  }

  win.ZUtils = ZUtils;

  /* =======================================================================
     2 — ZToast
     A queued stack of transient messages. component-toast.css owns every
     pixel; this owns the queue, the timers and the keyboard.

     window.ZToast.show({ title, text, variant, image, duration, actions })
       variant  'success' | 'error' | 'info'   (default 'info')
       image    an image url — rendered in the bordered square
       duration ms; 0 keeps the message until it is dismissed
       actions  [{ label, url }] rendered as small outline buttons

     At most three messages are on screen at once. Anything raised beyond that
     waits in a queue and appears as soon as a slot frees up.
     ======================================================================= */

  var TOAST_MAX_VISIBLE = 3;
  var TOAST_DEFAULT_DURATION = 4200;
  var TOAST_QUEUE_LIMIT = 12;

  var TOAST_ICONS = {
    success: '<path d="M20 6 9 17l-5-5"/>',
    error: '<path d="M12 9v4"/><path d="M12 17h.01"/>' +
      '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'
  };

  var TOAST_CLOSE_ICON = '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>';

  function toastSvg(paths, size) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '"' +
      ' viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"' +
      ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      paths +
      '</svg>'
    );
  }

  var ZToast = {
    stack: null,
    visible: [],
    queue: [],

    /** Called by <toast-stack> when it connects, and by show() as a fallback. */
    register: function (element) {
      if (!element) return;
      ZToast.stack = element;
      ZToast.flush();
    },

    unregister: function (element) {
      if (ZToast.stack === element) ZToast.stack = null;
    },

    host: function () {
      if (ZToast.stack && doc.contains(ZToast.stack)) return ZToast.stack;
      var found = qs('toast-stack, .toast-stack');
      if (found) {
        ZToast.stack = found;
        return found;
      }
      if (!doc.body) return null;
      // The layout always renders one, but a section preview might not have.
      var created = doc.createElement('toast-stack');
      created.className = 'toast-stack';
      created.setAttribute('aria-live', 'polite');
      created.setAttribute('aria-atomic', 'false');
      doc.body.appendChild(created);
      ZToast.stack = created;
      return created;
    },

    show: function (options) {
      var opts = options || {};
      var message = {
        title: opts.title || '',
        text: opts.text || '',
        variant: opts.variant === 'success' || opts.variant === 'error' ? opts.variant : 'info',
        image: opts.image || '',
        duration: opts.duration === 0 ? 0 : toInt(opts.duration, TOAST_DEFAULT_DURATION),
        actions: Array.isArray(opts.actions) ? opts.actions.slice(0, 2) : []
      };

      if (!message.title && !message.text) return null;

      // The message is also spoken, because a toast that only exists visually
      // is invisible to anyone using a screen reader with the page.
      ZUtils.announce(
        [message.title, message.text].filter(Boolean).join('. '),
        message.variant === 'error' ? 'assertive' : 'polite'
      );

      if (ZToast.visible.length >= TOAST_MAX_VISIBLE) {
        if (ZToast.queue.length < TOAST_QUEUE_LIMIT) ZToast.queue.push(message);
        return null;
      }

      return ZToast.render(message);
    },

    render: function (message) {
      var host = ZToast.host();
      if (!host) return null;

      var reduced = ZUtils.prefersReducedMotion();

      var card = doc.createElement('div');
      card.className = 'toast toast--' + message.variant;
      card.setAttribute('role', message.variant === 'error' ? 'alert' : 'status');
      card.setAttribute('data-variant', message.variant);
      if (message.duration > 0) {
        card.style.setProperty('--toast-duration', message.duration + 'ms');
      }

      /* Media: a product thumbnail when there is one, the variant glyph when
         there is not. Both are decorative — the words carry the meaning. */
      if (message.image) {
        var media = doc.createElement('span');
        media.className = 'toast__media';
        var img = doc.createElement('img');
        img.src = message.image;
        img.alt = '';
        img.width = 88;
        img.height = 88;
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
        card.appendChild(media);
      } else {
        var icon = doc.createElement('span');
        icon.className = 'toast__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = toastSvg(TOAST_ICONS[message.variant] || TOAST_ICONS.info, 16);
        card.appendChild(icon);
      }

      var body = doc.createElement('div');
      body.className = 'toast__body';

      if (message.title) {
        var title = doc.createElement('p');
        title.className = 'toast__title';
        title.textContent = message.title;
        body.appendChild(title);
      }

      if (message.text) {
        var text = doc.createElement('p');
        text.className = 'toast__text';
        text.textContent = message.text;
        body.appendChild(text);
      }

      if (message.actions.length) {
        var actions = doc.createElement('div');
        actions.className = 'toast__actions';
        message.actions.forEach(function (action) {
          if (!action || !action.label || !action.url) return;
          var link = doc.createElement('a');
          link.className = 'btn btn--outline btn--sm';
          link.href = action.url;
          link.textContent = action.label;
          actions.appendChild(link);
        });
        if (actions.childNodes.length) body.appendChild(actions);
      }

      card.appendChild(body);

      var close = doc.createElement('button');
      close.type = 'button';
      close.className = 'toast__close';
      close.setAttribute('data-toast-close', '');
      close.setAttribute('aria-label', string('a11y', 'close', 'Close'));
      close.innerHTML = toastSvg(TOAST_CLOSE_ICON, 15);
      card.appendChild(close);

      if (message.duration > 0 && !reduced) {
        var bar = doc.createElement('span');
        bar.className = 'toast__bar';
        bar.setAttribute('aria-hidden', 'true');
        card.appendChild(bar);
      }

      host.appendChild(card);

      var entry = {
        node: card,
        duration: message.duration,
        remaining: message.duration,
        startedAt: Date.now(),
        timer: null,
        paused: false
      };
      ZToast.visible.push(entry);

      function dismiss() {
        ZToast.dismiss(entry);
      }

      on(close, 'click', dismiss);

      /* Hovering, or tabbing into, a message stops its clock. Reading a
         message must never be a race. */
      on(card, 'mouseenter', function () {
        ZToast.pause(entry);
      });
      on(card, 'mouseleave', function () {
        ZToast.resume(entry);
      });
      on(card, 'focusin', function () {
        ZToast.pause(entry);
      });
      on(card, 'focusout', function (event) {
        if (card.contains(event.relatedTarget)) return;
        ZToast.resume(entry);
      });

      // Escape dismisses the message the keyboard is currently inside.
      on(card, 'keydown', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.stopPropagation();
          dismiss();
        }
      });

      ZToast.startTimer(entry);
      return card;
    },

    startTimer: function (entry) {
      if (!entry || entry.duration <= 0) return;
      if (entry.timer) win.clearTimeout(entry.timer);
      entry.startedAt = Date.now();
      entry.timer = win.setTimeout(function () {
        ZToast.dismiss(entry);
      }, Math.max(400, entry.remaining));
    },

    pause: function (entry) {
      if (!entry || entry.paused || entry.duration <= 0) return;
      entry.paused = true;
      if (entry.timer) win.clearTimeout(entry.timer);
      entry.timer = null;
      entry.remaining = Math.max(600, entry.remaining - (Date.now() - entry.startedAt));
      var bar = qs('.toast__bar', entry.node);
      if (bar) bar.style.animationPlayState = 'paused';
    },

    resume: function (entry) {
      if (!entry || !entry.paused || entry.duration <= 0) return;
      entry.paused = false;
      var bar = qs('.toast__bar', entry.node);
      if (bar) bar.style.animationPlayState = 'running';
      ZToast.startTimer(entry);
    },

    dismiss: function (entry) {
      if (!entry || entry.dismissed) return;
      entry.dismissed = true;
      if (entry.timer) win.clearTimeout(entry.timer);
      entry.timer = null;

      var index = ZToast.visible.indexOf(entry);
      if (index !== -1) ZToast.visible.splice(index, 1);

      var node = entry.node;
      var reduced = ZUtils.prefersReducedMotion();

      function remove() {
        if (node && node.parentNode) node.parentNode.removeChild(node);
        ZToast.flush();
      }

      if (!node) {
        ZToast.flush();
        return;
      }

      // Returning focus to the page before the card leaves prevents the
      // "focus fell to <body>" jump when a toast is dismissed from the keyboard.
      if (node.contains(doc.activeElement)) focusSafely(doc.body);

      if (reduced) {
        remove();
        return;
      }

      node.classList.add('is-out');
      node.setAttribute('data-state', 'out');
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        remove();
      }
      on(node, 'animationend', finish);
      win.setTimeout(finish, 600);
    },

    /** Move queued messages into free slots. */
    flush: function () {
      while (ZToast.queue.length && ZToast.visible.length < TOAST_MAX_VISIBLE) {
        ZToast.render(ZToast.queue.shift());
      }
    },

    /** Dismiss everything — used when an overlay takes over the screen. */
    clear: function () {
      ZToast.queue.length = 0;
      ZToast.visible.slice().forEach(function (entry) {
        ZToast.dismiss(entry);
      });
    }
  };

  win.ZToast = ZToast;

  /* =======================================================================
     3 — Custom element plumbing
     ======================================================================= */

  var hasCustomElements = 'customElements' in win && isFn(win.customElements.define);

  /**
   * Define an element once. The Theme Editor can evaluate this file again
   * after a section reload, and a second define() with the same name throws;
   * the guard is what makes the file safe to re-run.
   */
  function define(name, ctor) {
    if (!hasCustomElements || !name || !ctor) return;
    try {
      if (win.customElements.get(name)) return;
      win.customElements.define(name, ctor);
    } catch (error) {
      /* an already-defined name in a host we do not control is not fatal */
    }
  }

  /**
   * A tiny base class: it collects listeners, observers, timers and frames so
   * disconnectedCallback is a single call and nothing is ever left running.
   */
  var ZElement = (function () {
    // A named class so devtools shows something useful, wrapped in a factory
    // so a browser without HTMLElement never evaluates the extends clause.
    if (typeof HTMLElement !== 'function') return function () {};
    return class ZyrexElement extends HTMLElement {};
  })();

  ZElement.prototype.listen = function (target, type, handler, options) {
    if (!target) return handler;
    if (!this._zListeners) this._zListeners = [];
    on(target, type, handler, options);
    this._zListeners.push([target, type, handler, options]);
    return handler;
  };

  ZElement.prototype.unlisten = function () {
    (this._zListeners || []).forEach(function (item) {
      off(item[0], item[1], item[2], item[3]);
    });
    this._zListeners = [];
  };

  ZElement.prototype.observe = function (observer) {
    if (!observer) return observer;
    if (!this._zObservers) this._zObservers = [];
    this._zObservers.push(observer);
    return observer;
  };

  ZElement.prototype.timer = function (id) {
    if (!this._zTimers) this._zTimers = [];
    if (id) this._zTimers.push(id);
    return id;
  };

  ZElement.prototype.frame = function (id) {
    if (!this._zFrames) this._zFrames = [];
    if (id) this._zFrames.push(id);
    return id;
  };

  ZElement.prototype.teardown = function () {
    this.unlisten();
    (this._zObservers || []).forEach(function (observer) {
      try {
        if (isFn(observer.disconnect)) observer.disconnect();
        else if (isFn(observer.cancel)) observer.cancel();
      } catch (error) {
        /* an observer that is already gone needs nothing */
      }
    });
    this._zObservers = [];
    (this._zTimers || []).forEach(function (id) {
      win.clearTimeout(id);
      win.clearInterval(id);
    });
    this._zTimers = [];
    (this._zFrames || []).forEach(cancelRaf);
    this._zFrames = [];
  };

  /** True while any overlay owns the screen. Consulted by the sticky header. */
  function overlayOpen() {
    if (!doc.body) return false;
    if (doc.body.hasAttribute('data-cart-drawer-open')) return true;
    if (doc.body.hasAttribute('data-modal-open')) return true;
    if (doc.body.hasAttribute('data-nav-open')) return true;
    if (doc.body.classList.contains('no-scroll')) return true;
    return false;
  }

  /** The published header height, in pixels, for scroll offsets. */
  function headerHeight() {
    var value = 0;
    try {
      value = parseFloat(win.getComputedStyle(root).getPropertyValue('--header-h'));
    } catch (error) {
      value = 0;
    }
    if (!isFinite(value) || value <= 0) {
      var header = qs('sticky-header, .header');
      value = header ? header.getBoundingClientRect().height : 72;
    }
    // A rem value slips through as a small number; treat anything under 24 as rem.
    if (value > 0 && value < 24) value = value * 16;
    return value;
  }

  /** Scroll an element into view under the sticky header. */
  function scrollToElement(target, extra) {
    if (!target || !isFn(target.getBoundingClientRect)) return;
    var offset = headerHeight() + (extra === undefined ? 24 : extra);
    var top = target.getBoundingClientRect().top + win.pageYOffset - offset;
    var behavior = ZUtils.prefersReducedMotion() ? 'auto' : 'smooth';
    try {
      win.scrollTo({ top: Math.max(0, top), behavior: behavior });
    } catch (error) {
      win.scrollTo(0, Math.max(0, top));
    }
  }

  /* =======================================================================
     4 — <sticky-header>
     A 1:1 port of the reference header's scroll behaviour: transparent until
     the page has moved 12px, then a translucent paper background with a
     blurred backdrop and an ink bottom border.

     It also owns two jobs the reference did not need:
       - publishing the real header height to :root as `--header-h`, so every
         sticky column, anchor offset and drawer top in the theme is correct
         even when the merchant changes the logo size;
       - standing still while a drawer, a modal or the mobile menu is open, so
         the bar cannot restyle itself underneath an overlay.

     Optional children, all absent-safe:
       [data-header-bar]      the fixed-height row — measured for --header-h
       [data-nav-list]        the desktop nav, host of the sliding pill
       [data-nav-indicator]   the pill itself, driven by --ind-x / --ind-w
     ======================================================================= */

  class StickyHeader extends ZElement {
    connectedCallback() {
      this.threshold = toInt(this.getAttribute('data-threshold'), 12);
      this.stuck = null;
      this.paused = false;

      this.bar = qs('[data-header-bar]', this) || this.firstElementChild || this;
      this.navList = qs('[data-nav-list]', this);
      this.indicator = qs('[data-nav-indicator]', this);

      this.onScroll = ZUtils.rafThrottle(this.update.bind(this));
      this.onResize = ZUtils.debounce(this.measure.bind(this), 120);

      this.listen(win, 'scroll', this.onScroll, { passive: true });
      this.listen(win, 'resize', this.onResize);
      this.listen(doc, 'cart:opened', this.onOverlayChange.bind(this));
      this.listen(doc, 'cart:closed', this.onOverlayChange.bind(this));
      this.listen(doc, 'zyrex:overlay', this.onOverlayChange.bind(this));

      if (isFn(win.ResizeObserver)) {
        var self = this;
        this.ro = new win.ResizeObserver(function () {
          self.measure();
        });
        try {
          this.ro.observe(this.bar);
        } catch (error) {
          /* an unobservable node simply keeps the CSS default */
        }
        this.observe(this.ro);
      }

      this.setupIndicator();
      this.measure();
      this.update();
    }

    disconnectedCallback() {
      this.teardown();
      if (this.onScroll && isFn(this.onScroll.cancel)) this.onScroll.cancel();
      if (this.onResize && isFn(this.onResize.cancel)) this.onResize.cancel();
      this.stuck = null;
    }

    /** Publish the bar height. The mobile menu must not inflate it. */
    measure() {
      var node = this.bar && doc.contains(this.bar) ? this.bar : this;
      var height = node.getBoundingClientRect().height;
      if (!isFinite(height) || height <= 0) return;
      root.style.setProperty('--header-h', Math.round(height) + 'px');
      this.syncIndicator();
    }

    onOverlayChange() {
      // Re-read the scroll position the moment the screen is handed back.
      if (!overlayOpen()) this.update();
    }

    update() {
      if (overlayOpen()) return;
      var scrolled = (win.pageYOffset || root.scrollTop || 0) > this.threshold;
      if (scrolled === this.stuck) return;
      this.stuck = scrolled;
      this.classList.toggle('is-stuck', scrolled);
      this.setAttribute('data-stuck', scrolled ? 'true' : 'false');
    }

    /* ---- The sliding pill ----------------------------------------------
       The reference animates a shared layout element between nav items. The
       same effect here is one absolutely positioned pill whose x and width
       are two custom properties, so the CSS owns the easing. */

    setupIndicator() {
      if (!this.navList || !this.indicator) return;

      var self = this;
      this.listen(this.navList, 'pointerover', function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a') : null;
        if (link && self.navList.contains(link)) self.moveIndicator(link);
      });
      this.listen(this.navList, 'pointerleave', function () {
        self.syncIndicator();
      });
      this.listen(this.navList, 'focusin', function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a') : null;
        if (link) self.moveIndicator(link);
      });
      this.listen(this.navList, 'focusout', function (event) {
        if (self.navList.contains(event.relatedTarget)) return;
        self.syncIndicator();
      });

      this.frame(raf(function () {
        self.syncIndicator();
      }));
    }

    /** Park the pill on the current page's link, or hide it if there is none. */
    syncIndicator() {
      if (!this.navList || !this.indicator) return;
      var current =
        qs('[aria-current="page"]', this.navList) || qs('.is-active', this.navList);
      if (!current) {
        this.indicator.setAttribute('data-visible', 'false');
        return;
      }
      this.moveIndicator(current);
    }

    moveIndicator(link) {
      if (!this.navList || !this.indicator || !link) return;
      var listBox = this.navList.getBoundingClientRect();
      var linkBox = link.getBoundingClientRect();
      if (!linkBox.width) return;
      this.indicator.setAttribute('data-visible', 'true');
      this.indicator.style.setProperty('--ind-x', Math.round(linkBox.left - listBox.left) + 'px');
      this.indicator.style.setProperty('--ind-w', Math.round(linkBox.width) + 'px');
    }
  }

  define('sticky-header', StickyHeader);

  /* =======================================================================
     5 — <nav-drawer>
     The mobile menu. It expands under the header from height 0 to its natural
     height in .28s on cubic-bezier(.16,1,.3,1) — the reference's exact curve
     — with the links entering one after another.

     Expected children, all optional:
       [data-nav-toggle]   the hamburger (may also live outside, matched by
                           aria-controls pointing at this element's id)
       [data-nav-panel]    the sheet that expands
       [data-nav-close]    any extra close control
     ======================================================================= */

  var NAV_DURATION = 280;
  var NAV_STAGGER = 40;

  class NavDrawer extends ZElement {
    connectedCallback() {
      this.panel = qs('[data-nav-panel]', this) || qs('nav', this);
      this.toggleButton =
        qs('[data-nav-toggle]', this) ||
        (this.id ? qs('[data-nav-toggle][aria-controls="' + this.id + '"]') : null);

      this.isOpen = false;
      this.opener = null;
      this.trap = null;
      this.transitionTimer = null;

      if (this.panel) {
        this.panel.style.overflow = 'hidden';
        this.panel.style.height = '0px';
        this.panel.style.visibility = 'hidden';
        this.panel.setAttribute('aria-hidden', 'true');
        this.panel.setAttribute('inert', '');
        this.links = qsa('a, button', this.panel);
        this.links.forEach(function (link, index) {
          link.style.setProperty('--i', String(index));
        });
      } else {
        this.links = [];
      }

      this.setAttribute('data-state', 'closed');

      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-expanded', 'false');
        if (this.id && !this.toggleButton.getAttribute('aria-controls')) {
          this.toggleButton.setAttribute('aria-controls', this.id);
        }
        this.listen(this.toggleButton, 'click', this.onToggleClick.bind(this));
      }

      var self = this;
      qsa('[data-nav-close]', this).forEach(function (node) {
        self.listen(node, 'click', function () {
          self.close();
        });
      });

      this.listen(doc, 'keydown', this.onKeydown.bind(this), true);
      this.listen(this, 'click', this.onPanelClick.bind(this));
      this.listen(win, 'resize', ZUtils.debounce(this.onResize.bind(this), 150));
    }

    disconnectedCallback() {
      if (this.isOpen) this.close(true);
      this.teardown();
    }

    onToggleClick(event) {
      event.preventDefault();
      if (this.isOpen) this.close();
      else this.open(event.currentTarget);
    }

    onPanelClick(event) {
      if (!this.isOpen) return;
      var link = event.target && event.target.closest ? event.target.closest('a') : null;
      // Tapping a link is what dismisses the sheet, exactly as in the
      // reference: watching the route would also fire on a back navigation
      // the visitor did not start from this menu.
      if (link && this.panel && this.panel.contains(link)) this.close(true);
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
      }
    }

    onResize() {
      if (!this.isOpen || !this.panel) return;
      // A rotation to landscape can put the desktop nav back on screen.
      if (this.toggleButton && this.toggleButton.offsetParent === null) {
        this.close(true);
        return;
      }
      this.panel.style.height = 'auto';
    }

    open(opener) {
      if (this.isOpen || !this.panel) return;
      this.isOpen = true;
      this.opener = opener || this.toggleButton || null;

      if (this.transitionTimer) win.clearTimeout(this.transitionTimer);

      this.setAttribute('data-state', 'open');
      if (doc.body) doc.body.setAttribute('data-nav-open', 'true');
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-expanded', 'true');
        this.toggleButton.setAttribute(
          'aria-label',
          string('a11y', 'close', 'Close') + ' — ' + string('a11y', 'menu', 'Menu')
        );
      }

      this.panel.removeAttribute('inert');
      this.panel.removeAttribute('aria-hidden');
      this.panel.style.visibility = 'visible';

      var reduced = ZUtils.prefersReducedMotion();
      var target = this.panel.scrollHeight;

      if (reduced) {
        this.panel.style.transition = 'none';
        this.panel.style.height = 'auto';
      } else {
        this.panel.style.transition = 'height ' + NAV_DURATION + 'ms cubic-bezier(.16,1,.3,1)';
        this.panel.style.height = target + 'px';
        var self = this;
        this.transitionTimer = this.timer(
          win.setTimeout(function () {
            if (self.isOpen && self.panel) self.panel.style.height = 'auto';
          }, NAV_DURATION + 20)
        );
      }

      this.staggerLinks(true);
      ZUtils.lockScroll();
      this.trap = ZUtils.trapFocus(this.panel, ZUtils.getFocusable(this.panel)[0] || this.panel);
      emit(doc, 'zyrex:overlay', { open: true, source: 'nav-drawer' });
      emit(this, 'nav-drawer:opened', {});
    }

    close(immediate) {
      if (!this.isOpen) return;
      this.isOpen = false;

      if (this.transitionTimer) win.clearTimeout(this.transitionTimer);

      this.setAttribute('data-state', 'closed');
      if (doc.body) doc.body.removeAttribute('data-nav-open');
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-expanded', 'false');
        this.toggleButton.setAttribute('aria-label', string('a11y', 'openMenu', 'Open menu'));
      }

      var reduced = ZUtils.prefersReducedMotion() || immediate === true;

      if (this.panel) {
        this.panel.setAttribute('aria-hidden', 'true');
        this.panel.setAttribute('inert', '');

        if (reduced) {
          this.panel.style.transition = 'none';
          this.panel.style.height = '0px';
          this.panel.style.visibility = 'hidden';
        } else {
          // From `auto` to a number, then to zero on the next frame.
          this.panel.style.height = this.panel.scrollHeight + 'px';
          var panel = this.panel;
          var self = this;
          this.frame(
            raf(function () {
              panel.style.transition = 'height ' + NAV_DURATION + 'ms cubic-bezier(.16,1,.3,1)';
              panel.style.height = '0px';
            })
          );
          this.transitionTimer = this.timer(
            win.setTimeout(function () {
              if (!self.isOpen && panel) panel.style.visibility = 'hidden';
            }, NAV_DURATION + 20)
          );
        }
      }

      this.staggerLinks(false);
      ZUtils.unlockScroll();
      if (this.trap) {
        ZUtils.removeTrapFocus(this.opener);
        this.trap = null;
      }
      this.opener = null;
      emit(doc, 'zyrex:overlay', { open: false, source: 'nav-drawer' });
      emit(this, 'nav-drawer:closed', {});
    }

    /** The x:-14px → 0 entrance from the reference, one link at a time. */
    staggerLinks(opening) {
      if (!this.links || !this.links.length) return;
      var reduced = ZUtils.prefersReducedMotion();
      this.links.forEach(function (link, index) {
        if (!opening || reduced) {
          link.style.transitionDelay = '';
          link.classList.toggle('is-in', opening);
          return;
        }
        link.style.transitionDelay = index * NAV_STAGGER + 'ms';
        link.classList.remove('is-in');
      });

      if (!opening || reduced) return;

      var links = this.links;
      this.frame(
        raf(function () {
          links.forEach(function (link) {
            link.classList.add('is-in');
          });
        })
      );
    }
  }

  define('nav-drawer', NavDrawer);

  /* =======================================================================
     6 — <details-disclosure>
     A <details> element that opens and closes on grid-template-rows instead
     of snapping. Closing waits for the animation before `open` is removed, so
     the content is never cut off mid-height.

     Closes on Escape and on a click outside itself, which is what a menu
     dropdown in the header needs.
     ======================================================================= */

  class DetailsDisclosure extends ZElement {
    connectedCallback() {
      this.details = qs('details', this);
      if (!this.details) return;

      this.summary = qs('summary', this.details);
      this.panel =
        qs('[data-disclosure-panel]', this.details) ||
        (this.summary ? this.summary.nextElementSibling : null);
      this.animating = false;

      if (this.summary) {
        this.summary.setAttribute('role', this.summary.getAttribute('role') || 'button');
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        this.listen(this.summary, 'click', this.onSummaryClick.bind(this));
      }

      this.prepPanel();

      this.listen(doc, 'keydown', this.onKeydown.bind(this));
      this.listen(doc, 'click', this.onDocumentClick.bind(this));
      this.listen(this.details, 'toggle', this.onToggle.bind(this));
    }

    disconnectedCallback() {
      this.teardown();
    }

    /** Give the panel the grid it animates on, unless CSS already did. */
    prepPanel() {
      if (!this.panel) return;
      var display = '';
      try {
        display = win.getComputedStyle(this.panel).display;
      } catch (error) {
        display = '';
      }
      if (display !== 'grid') {
        this.panel.style.display = 'grid';
        this.panel.style.transition = 'grid-template-rows var(--dur-2, .28s) var(--ease-out, ease)';
      }
      this.panel.style.gridTemplateRows = this.details.open ? '1fr' : '0fr';
      var inner = this.panel.firstElementChild;
      if (inner) {
        inner.style.overflow = 'hidden';
        inner.style.minHeight = '0';
      }
    }

    onSummaryClick(event) {
      if (!this.details || !this.panel) return;
      event.preventDefault();
      if (this.details.open) this.hide();
      else this.show();
    }

    onToggle() {
      if (!this.summary || !this.details) return;
      this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
    }

    onKeydown(event) {
      if (!this.details || !this.details.open) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.stopPropagation();
      this.hide();
      focusSafely(this.summary);
    }

    onDocumentClick(event) {
      if (!this.details || !this.details.open) return;
      if (this.contains(event.target)) return;
      this.hide();
    }

    show() {
      if (!this.details || !this.panel) return;
      this.details.open = true;
      this.setAttribute('data-state', 'open');
      var panel = this.panel;
      if (ZUtils.prefersReducedMotion()) {
        panel.style.gridTemplateRows = '1fr';
        return;
      }
      panel.style.gridTemplateRows = '0fr';
      this.frame(
        raf(function () {
          panel.style.gridTemplateRows = '1fr';
        })
      );
    }

    hide() {
      if (!this.details || !this.panel) return;
      this.setAttribute('data-state', 'closed');
      var details = this.details;
      var panel = this.panel;

      if (ZUtils.prefersReducedMotion()) {
        panel.style.gridTemplateRows = '0fr';
        details.open = false;
        return;
      }

      panel.style.gridTemplateRows = '0fr';
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        off(panel, 'transitionend', onEnd);
        details.open = false;
      }
      function onEnd(event) {
        if (event.propertyName && event.propertyName.indexOf('grid-template-rows') === -1) return;
        finish();
      }
      on(panel, 'transitionend', onEnd);
      this.timer(win.setTimeout(finish, 500));
    }
  }

  define('details-disclosure', DetailsDisclosure);

  /* =======================================================================
     7 — <accordion-group>
     Buttons and panels rather than <details>, because that is what base.css
     styles and what lets the plus rotate 45° from `aria-expanded`.

     Attributes
       data-single         only one panel open at a time
       data-open-first     open the first item on load
       data-deep-link      honour and write #hashes (default on)

     Markup it expects, all absent-safe:
       .accordion__item
         button.accordion__trigger[aria-expanded][aria-controls]
         .accordion__panel[id]
     ======================================================================= */

  class AccordionGroup extends ZElement {
    connectedCallback() {
      this.single = attrOn(this, 'data-single');
      this.deepLink = this.getAttribute('data-deep-link') !== 'false';
      this.triggers = qsa('[data-accordion-trigger], .accordion__trigger', this);

      var self = this;
      this.triggers.forEach(function (trigger, index) {
        var panel = self.panelFor(trigger);
        if (!panel) return;

        if (!panel.id) panel.id = (self.id || 'accordion') + '-panel-' + index;
        if (!trigger.id) trigger.id = panel.id + '-trigger';
        trigger.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', panel.getAttribute('role') || 'region');
        panel.setAttribute('aria-labelledby', trigger.id);

        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        self.paint(panel, open);

        self.listen(trigger, 'click', function (event) {
          event.preventDefault();
          self.toggle(trigger);
        });
        self.listen(trigger, 'keydown', function (event) {
          self.onTriggerKeydown(event, index);
        });
      });

      if (attrOn(this, 'data-open-first') && this.triggers.length) {
        this.setOpen(this.triggers[0], true);
      }

      if (this.deepLink) {
        this.listen(win, 'hashchange', this.openFromHash.bind(this));
        var element = this;
        this.timer(
          win.setTimeout(function () {
            element.openFromHash(true);
          }, 40)
        );
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    panelFor(trigger) {
      if (!trigger) return null;
      var id = trigger.getAttribute('aria-controls');
      if (id) {
        var byId = doc.getElementById(id);
        if (byId) return byId;
      }
      var item = trigger.closest ? trigger.closest('.accordion__item, [data-accordion-item]') : null;
      if (item) {
        var inItem = qs('.accordion__panel, [data-accordion-panel]', item);
        if (inItem) return inItem;
      }
      return trigger.nextElementSibling;
    }

    /** The panel's open state, expressed the way base.css expects it. */
    paint(panel, open) {
      if (!panel) return;
      panel.classList.toggle('is-open', open);
      panel.setAttribute('data-open', open ? 'true' : 'false');
      // `hidden` is never used: the reset makes it a real display:none, which
      // cannot be transitioned. `inert` keeps the closed content untabbable.
      if (open) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    }

    setOpen(trigger, open) {
      if (!trigger) return;
      var panel = this.panelFor(trigger);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      var item = trigger.closest ? trigger.closest('.accordion__item, [data-accordion-item]') : null;
      if (item) item.classList.toggle('is-open', open);
      this.paint(panel, open);
    }

    toggle(trigger) {
      var open = trigger.getAttribute('aria-expanded') === 'true';
      if (this.single && !open) {
        var self = this;
        this.triggers.forEach(function (other) {
          if (other !== trigger) self.setOpen(other, false);
        });
      }
      this.setOpen(trigger, !open);
      emit(this, 'accordion:toggled', { trigger: trigger, open: !open });
    }

    /** Up/Down move between headers, Home/End jump to the ends. */
    onTriggerKeydown(event, index) {
      var keys = {
        ArrowDown: 1,
        Down: 1,
        ArrowUp: -1,
        Up: -1
      };

      if (event.key === 'Home') {
        event.preventDefault();
        focusSafely(this.triggers[0]);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        focusSafely(this.triggers[this.triggers.length - 1]);
        return;
      }

      var step = keys[event.key];
      if (!step) return;
      event.preventDefault();
      var next = (index + step + this.triggers.length) % this.triggers.length;
      focusSafely(this.triggers[next]);
    }

    /** #some-question in the address bar opens and scrolls to that item. */
    openFromHash(initial) {
      var hash = win.location.hash ? win.location.hash.slice(1) : '';
      if (!hash) return;

      var target = null;
      try {
        target = doc.getElementById(hash);
      } catch (error) {
        target = null;
      }
      if (!target || !this.contains(target)) return;

      var trigger = null;
      if (this.triggers.indexOf(target) !== -1) trigger = target;
      else {
        var item = target.closest
          ? target.closest('.accordion__item, [data-accordion-item]')
          : null;
        if (item) trigger = qs('[data-accordion-trigger], .accordion__trigger', item);
        else trigger = qs('[aria-controls="' + hash + '"]', this);
      }
      if (!trigger) return;

      if (this.single) {
        var self = this;
        this.triggers.forEach(function (other) {
          if (other !== trigger) self.setOpen(other, false);
        });
      }
      this.setOpen(trigger, true);

      if (initial) scrollToElement(trigger, 28);
      focusSafely(trigger);
    }
  }

  define('accordion-group', AccordionGroup);

  /* =======================================================================
     8 — <tabs-group>
     Full WAI-ARIA tabs. Arrow keys move and activate, Home and End jump to
     the ends, and the pill indicator follows on --ind-x / --ind-w so the CSS
     keeps ownership of the easing.
     ======================================================================= */

  class TabsGroup extends ZElement {
    connectedCallback() {
      this.list = qs('[role="tablist"], .tab-list', this);
      this.tabs = qsa('[role="tab"], .tab', this);
      this.indicator = qs('[data-tab-indicator]', this);
      this.vertical = this.getAttribute('data-orientation') === 'vertical';

      if (!this.tabs.length) return;

      if (this.list) {
        this.list.setAttribute('role', 'tablist');
        if (this.vertical) this.list.setAttribute('aria-orientation', 'vertical');
      }

      var self = this;
      var selectedIndex = 0;

      this.tabs.forEach(function (tab, index) {
        tab.setAttribute('role', 'tab');
        if (!tab.id) tab.id = (self.id || 'tabs') + '-tab-' + index;

        var panel = self.panelFor(tab);
        if (panel) {
          if (!panel.id) panel.id = tab.id + '-panel';
          tab.setAttribute('aria-controls', panel.id);
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
          if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
        }

        if (tab.getAttribute('aria-selected') === 'true') selectedIndex = index;

        self.listen(tab, 'click', function (event) {
          event.preventDefault();
          self.select(index, true);
        });
        self.listen(tab, 'keydown', function (event) {
          self.onKeydown(event, index);
        });
      });

      this.select(selectedIndex, false);

      this.onResize = ZUtils.debounce(this.moveIndicator.bind(this), 120);
      this.listen(win, 'resize', this.onResize);

      var element = this;
      this.frame(
        raf(function () {
          element.moveIndicator();
        })
      );
    }

    disconnectedCallback() {
      this.teardown();
      if (this.onResize && isFn(this.onResize.cancel)) this.onResize.cancel();
    }

    panelFor(tab) {
      if (!tab) return null;
      var id = tab.getAttribute('aria-controls') || tab.getAttribute('data-panel');
      if (id) {
        var byId = doc.getElementById(id);
        if (byId) return byId;
      }
      var panels = qsa('[role="tabpanel"], .tab-panel', this);
      return panels[this.tabs.indexOf(tab)] || null;
    }

    select(index, focus) {
      if (!this.tabs.length) return;
      var next = clamp(index, 0, this.tabs.length - 1);
      var self = this;

      this.tabs.forEach(function (tab, i) {
        var active = i === next;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        var panel = self.panelFor(tab);
        if (!panel) return;
        if (active) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', 'hidden');
      });

      this.selectedIndex = next;
      if (focus) focusSafely(this.tabs[next]);
      this.moveIndicator();
      emit(this, 'tabs:changed', { index: next, tab: this.tabs[next] });
    }

    onKeydown(event, index) {
      var forward = this.vertical ? 'ArrowDown' : 'ArrowRight';
      var back = this.vertical ? 'ArrowUp' : 'ArrowLeft';
      var last = this.tabs.length - 1;
      var next = null;

      if (event.key === forward || (this.vertical ? event.key === 'Down' : event.key === 'Right')) {
        next = index === last ? 0 : index + 1;
      } else if (event.key === back || (this.vertical ? event.key === 'Up' : event.key === 'Left')) {
        next = index === 0 ? last : index - 1;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = last;
      } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.select(index, true);
        return;
      }

      if (next === null) return;
      event.preventDefault();
      this.select(next, true);
    }

    moveIndicator() {
      if (!this.indicator || !this.list) return;
      var tab = this.tabs[this.selectedIndex || 0];
      if (!tab) return;
      var listBox = this.list.getBoundingClientRect();
      var tabBox = tab.getBoundingClientRect();
      if (!tabBox.width) return;
      this.indicator.style.setProperty(
        '--ind-x',
        Math.round(tabBox.left - listBox.left + (this.list.scrollLeft || 0)) + 'px'
      );
      this.indicator.style.setProperty('--ind-w', Math.round(tabBox.width) + 'px');
      this.indicator.setAttribute('data-visible', 'true');
    }
  }

  define('tabs-group', TabsGroup);

  /* =======================================================================
     9 — <slider-component>
     A scroll-snap rail with progressive enhancement on top: arrows that
     disable at the ends, generated dots, mouse drag with momentum, and
     optional autoplay that stops the moment a visitor looks at it.

     Attributes
       data-per-view / -md / -lg   slides visible per breakpoint
       data-autoplay="5000"        ms between advances (0 or absent = off)
       data-loop                   wrap past the ends
       data-drag="false"           opt out of mouse dragging

     Children, all optional:
       [data-slider-track] | .slider__track
       [data-slider-slide] | .slider__slide
       [data-slider-prev] [data-slider-next]
       [data-slider-dots] | .slider__dots
       [data-slider-status]        a live "3 of 8" region
     ======================================================================= */

  class SliderComponent extends ZElement {
    connectedCallback() {
      this.track = qs('[data-slider-track], .slider__track', this);
      if (!this.track) return;

      this.slides = qsa('[data-slider-slide], .slider__slide', this.track);
      if (!this.slides.length) {
        this.slides = Array.prototype.filter.call(this.track.children, function (node) {
          return node.nodeType === 1;
        });
      }

      this.prevButton = qs('[data-slider-prev]', this);
      this.nextButton = qs('[data-slider-next]', this);
      this.dotsHost = qs('[data-slider-dots], .slider__dots', this);
      this.status = qs('[data-slider-status]', this);

      this.index = 0;
      this.dragEnabled = this.getAttribute('data-drag') !== 'false';
      this.loop = attrOn(this, 'data-loop');
      this.autoplayDelay = toInt(this.getAttribute('data-autoplay'), 0);
      this.autoplayTimer = null;
      this.paused = false;
      this.momentumFrame = null;

      if (this.slides.length < 2) {
        this.setAttribute('data-single-slide', 'true');
      }

      this.applyPerView();
      this.buildDots();

      var self = this;
      if (this.dotsHost) this.listen(this.dotsHost, 'click', this.onDotsClick.bind(this));
      if (this.prevButton) {
        this.listen(this.prevButton, 'click', function (event) {
          event.preventDefault();
          self.pauseAutoplay(true);
          self.go(-1);
        });
      }
      if (this.nextButton) {
        this.listen(this.nextButton, 'click', function (event) {
          event.preventDefault();
          self.pauseAutoplay(true);
          self.go(1);
        });
      }

      this.onScroll = ZUtils.rafThrottle(this.sync.bind(this));
      this.listen(this.track, 'scroll', this.onScroll, { passive: true });

      this.onResize = ZUtils.debounce(function () {
        self.applyPerView();
        self.buildDots();
        self.sync();
      }, 150);
      this.listen(win, 'resize', this.onResize);

      this.listen(this.track, 'keydown', this.onKeydown.bind(this));

      if (this.dragEnabled) this.setupDrag();
      this.setupAutoplay();

      this.frame(
        raf(function () {
          self.sync();
        })
      );
    }

    disconnectedCallback() {
      this.stopAutoplay();
      cancelRaf(this.momentumFrame);
      this.momentumFrame = null;
      this.teardown();
      if (this.onScroll && isFn(this.onScroll.cancel)) this.onScroll.cancel();
      if (this.onResize && isFn(this.onResize.cancel)) this.onResize.cancel();
    }

    /* ---- Layout --------------------------------------------------------- */

    /** Publish `--slider-per` for the current breakpoint. */
    applyPerView() {
      var base = toFloat(this.getAttribute('data-per-view'), 0);
      var md = toFloat(this.getAttribute('data-per-view-md'), 0);
      var lg = toFloat(this.getAttribute('data-per-view-lg'), 0);
      if (!base && !md && !lg) return;

      var width = win.innerWidth || root.clientWidth || 0;
      var per = base || 1;
      if (width >= 768 && md) per = md;
      if (width >= 1024 && lg) per = lg;
      this.style.setProperty('--slider-per', String(per));
      this.perView = per;
    }

    /**
     * How many slides actually fit, measured rather than assumed. The step is
     * taken from two neighbours so the gap between slides is included — using
     * one slide's width alone over-counts by a column on a wide rail.
     */
    visibleCount() {
      if (!this.slides.length || !this.track) return 1;
      var step =
        this.slides.length > 1
          ? this.slides[1].offsetLeft - this.slides[0].offsetLeft
          : this.slides[0].getBoundingClientRect().width;
      if (!step || step < 1) return 1;
      var count = Math.round(this.track.clientWidth / step);
      return clamp(count, 1, this.slides.length);
    }

    pageCount() {
      var per = this.visibleCount();
      return Math.max(1, Math.ceil(this.slides.length / per));
    }

    /* ---- Dots ----------------------------------------------------------- */

    buildDots() {
      if (!this.dotsHost) return;
      var pages = this.pageCount();

      if (pages < 2) {
        this.dotsHost.innerHTML = '';
        this.dotsHost.setAttribute('hidden', 'hidden');
        this.dots = [];
        return;
      }

      this.dotsHost.removeAttribute('hidden');
      if (this.dots && this.dots.length === pages) return;

      this.dotsHost.innerHTML = '';
      this.dots = [];

      for (var i = 0; i < pages; i += 1) {
        var dot = doc.createElement('button');
        dot.type = 'button';
        dot.className = 'slider__dot';
        dot.setAttribute('data-slider-dot', String(i));
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.setAttribute('aria-label', i + 1 + ' / ' + pages);
        this.dotsHost.appendChild(dot);
        this.dots.push(dot);
      }
    }

    /** Bound once, in connectedCallback, because the dots are rebuilt often. */
    onDotsClick(event) {
      var dot = event.target && event.target.closest ? event.target.closest('[data-slider-dot]') : null;
      if (!dot) return;
      event.preventDefault();
      this.pauseAutoplay(true);
      this.scrollToIndex(toInt(dot.getAttribute('data-slider-dot'), 0) * this.visibleCount());
    }

    /* ---- Movement ------------------------------------------------------- */

    go(direction) {
      var per = this.visibleCount();
      var max = Math.max(0, this.slides.length - per);
      var next = this.index + direction * per;

      if (next < 0) next = this.loop ? max : 0;
      if (next > max) next = this.loop ? 0 : max;

      this.scrollToIndex(next);
    }

    scrollToIndex(index) {
      if (!this.track || !this.slides.length) return;
      var target = this.slides[clamp(index, 0, this.slides.length - 1)];
      if (!target) return;

      var left = target.offsetLeft - this.track.offsetLeft;
      var behavior = ZUtils.prefersReducedMotion() ? 'auto' : 'smooth';
      try {
        this.track.scrollTo({ left: left, behavior: behavior });
      } catch (error) {
        this.track.scrollLeft = left;
      }
    }

    /** Read the rail and repaint the controls. Never writes scroll position. */
    sync() {
      if (!this.track || !this.slides.length) return;

      var scrollLeft = this.track.scrollLeft;
      var closest = 0;
      var smallest = Infinity;

      for (var i = 0; i < this.slides.length; i += 1) {
        var offset = Math.abs(this.slides[i].offsetLeft - this.track.offsetLeft - scrollLeft);
        if (offset < smallest) {
          smallest = offset;
          closest = i;
        }
      }
      this.index = closest;

      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var atStart = scrollLeft <= 2;
      var atEnd = scrollLeft >= maxScroll - 2;

      if (this.prevButton) this.setControl(this.prevButton, this.loop ? false : atStart);
      if (this.nextButton) this.setControl(this.nextButton, this.loop ? false : atEnd);

      if (this.dots && this.dots.length) {
        var page = clamp(Math.round(closest / this.visibleCount()), 0, this.dots.length - 1);
        this.dots.forEach(function (dot, i) {
          dot.setAttribute('aria-selected', i === page ? 'true' : 'false');
          dot.classList.toggle('is-active', i === page);
        });
      }

      if (this.status) {
        this.status.textContent = closest + 1 + ' / ' + this.slides.length;
      }

      this.setAttribute('data-index', String(closest));
    }

    setControl(button, disabled) {
      if (!button) return;
      if ('disabled' in button) button.disabled = !!disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.classList.toggle('is-disabled', !!disabled);
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight' || event.key === 'Right') {
        event.preventDefault();
        this.pauseAutoplay(true);
        this.go(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'Left') {
        event.preventDefault();
        this.pauseAutoplay(true);
        this.go(-1);
      }
    }

    /* ---- Drag ----------------------------------------------------------
       Pointer dragging is enabled for the mouse only. A touch screen already
       has native inertial scrolling that is smoother than anything scripted,
       and hijacking it would break the browser's own snap behaviour. */

    setupDrag() {
      if (!this.track || !win.PointerEvent) return;

      var self = this;
      var dragging = false;
      var startX = 0;
      var startScroll = 0;
      var lastX = 0;
      var lastTime = 0;
      var velocity = 0;
      var moved = false;

      function onDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target && event.target.closest && event.target.closest('a, button, input, select, textarea')) {
          return;
        }
        dragging = true;
        moved = false;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = Date.now();
        velocity = 0;
        startScroll = self.track.scrollLeft;
        cancelRaf(self.momentumFrame);
        self.momentumFrame = null;
        self.track.style.scrollSnapType = 'none';
        self.track.style.scrollBehavior = 'auto';
        self.classList.add('is-dragging');
        self.pauseAutoplay(true);
      }

      function onMove(event) {
        if (!dragging) return;
        var delta = event.clientX - startX;
        if (Math.abs(delta) > 4) moved = true;
        self.track.scrollLeft = startScroll - delta;

        var now = Date.now();
        var elapsed = now - lastTime;
        if (elapsed > 0) {
          velocity = (event.clientX - lastX) / elapsed;
          lastX = event.clientX;
          lastTime = now;
        }
      }

      function onUp() {
        if (!dragging) return;
        dragging = false;
        self.classList.remove('is-dragging');
        if (!moved) {
          self.releaseTrack();
          return;
        }
        self.momentum(velocity);
      }

      function onClickCapture(event) {
        // Swallow the click that ends a drag so a card does not navigate.
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      }

      this.listen(this.track, 'pointerdown', onDown);
      this.listen(win, 'pointermove', onMove, { passive: true });
      this.listen(win, 'pointerup', onUp);
      this.listen(win, 'pointercancel', onUp);
      this.listen(this.track, 'click', onClickCapture, true);
      this.listen(this.track, 'dragstart', function (event) {
        if (dragging) event.preventDefault();
      });
    }

    /** Decay the flick, then hand the rail back to CSS scroll-snap. */
    momentum(velocity) {
      var self = this;
      var speed = velocity * 16;

      if (ZUtils.prefersReducedMotion() || Math.abs(speed) < 2) {
        this.releaseTrack();
        this.scrollToIndex(this.nearestIndex());
        return;
      }

      function step() {
        speed *= 0.94;
        self.track.scrollLeft -= speed;
        if (Math.abs(speed) > 0.6) {
          self.momentumFrame = raf(step);
          return;
        }
        self.momentumFrame = null;
        self.releaseTrack();
        self.scrollToIndex(self.nearestIndex());
      }

      this.momentumFrame = raf(step);
    }

    releaseTrack() {
      if (!this.track) return;
      this.track.style.scrollSnapType = '';
      this.track.style.scrollBehavior = '';
      this.sync();
    }

    nearestIndex() {
      this.sync();
      return this.index;
    }

    /* ---- Autoplay ------------------------------------------------------- */

    setupAutoplay() {
      if (!this.autoplayDelay || this.slides.length < 2) return;
      if (ZUtils.prefersReducedMotion()) return;

      var self = this;
      this.listen(this, 'mouseenter', function () {
        self.pauseAutoplay();
      });
      this.listen(this, 'mouseleave', function () {
        self.resumeAutoplay();
      });
      this.listen(this, 'focusin', function () {
        self.pauseAutoplay();
      });
      this.listen(this, 'focusout', function (event) {
        if (self.contains(event.relatedTarget)) return;
        self.resumeAutoplay();
      });
      this.listen(doc, 'visibilitychange', function () {
        if (doc.hidden) self.pauseAutoplay();
        else self.resumeAutoplay();
      });

      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayDelay || this.autoplayTimer || this.stopped) return;
      var self = this;
      this.autoplayTimer = this.timer(
        win.setInterval(function () {
          if (doc.hidden || self.paused) return;
          var per = self.visibleCount();
          var max = Math.max(0, self.slides.length - per);
          if (self.index >= max) self.scrollToIndex(0);
          else self.go(1);
        }, Math.max(2000, this.autoplayDelay))
      );
    }

    /** `permanent` is used for a deliberate interaction: it never resumes. */
    pauseAutoplay(permanent) {
      this.paused = true;
      if (permanent) {
        this.stopped = true;
        this.stopAutoplay();
      }
    }

    resumeAutoplay() {
      if (this.stopped) return;
      this.paused = false;
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      win.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  define('slider-component', SliderComponent);

  /* =======================================================================
     10 — <quantity-input>
     The stepper. The number input carries the semantics; the two round
     buttons are a pointer convenience and are aria-hidden in the snippet,
     so nothing here adds a second announced control.

     Holding a button repeats, accelerating gently, and every path clamps to
     min and max before dispatching one `change` event for the cart to hear.
     ======================================================================= */

  var QTY_REPEAT_DELAY = 420;
  var QTY_REPEAT_FAST = 70;

  class QuantityInput extends ZElement {
    connectedCallback() {
      this.input = qs('input[type="number"], input[name="quantity"], input', this);
      if (!this.input) return;

      this.minus = qs('[data-qty-minus], [name="minus"]', this);
      this.plus = qs('[data-qty-plus], [name="plus"]', this);

      this.min = toInt(this.getAttribute('data-min'), toInt(this.input.getAttribute('min'), 1));
      var maxAttr = this.getAttribute('data-max') || this.input.getAttribute('max');
      this.max = maxAttr === null || maxAttr === '' ? Infinity : toInt(maxAttr, Infinity);
      this.step = Math.max(1, toInt(this.input.getAttribute('step'), 1));

      this.repeatTimer = null;
      this.repeatInterval = null;

      var self = this;
      if (this.minus) this.bindButton(this.minus, -1);
      if (this.plus) this.bindButton(this.plus, 1);

      this.listen(this.input, 'change', function () {
        self.clampValue(true);
      });
      this.listen(this.input, 'blur', function () {
        self.clampValue(true);
      });
      this.listen(this.input, 'keydown', function (event) {
        if (event.key === 'Enter') self.clampValue(true);
      });

      this.clampValue(false);
    }

    disconnectedCallback() {
      this.stopRepeat();
      this.teardown();
    }

    bindButton(button, direction) {
      var self = this;

      this.listen(button, 'click', function (event) {
        event.preventDefault();
        self.nudge(direction);
      });

      // Long-press repeat. pointerdown/up covers mouse, pen and touch alike.
      this.listen(button, 'pointerdown', function (event) {
        if (event.button !== undefined && event.button !== 0) return;
        self.startRepeat(direction);
      });
      this.listen(button, 'pointerup', function () {
        self.stopRepeat();
      });
      this.listen(button, 'pointerleave', function () {
        self.stopRepeat();
      });
      this.listen(button, 'pointercancel', function () {
        self.stopRepeat();
      });
      this.listen(win, 'blur', function () {
        self.stopRepeat();
      });
    }

    startRepeat(direction) {
      this.stopRepeat();
      var self = this;
      this.repeatTimer = this.timer(
        win.setTimeout(function () {
          var speed = QTY_REPEAT_FAST * 3;
          function tick() {
            self.nudge(direction);
            speed = Math.max(QTY_REPEAT_FAST, speed * 0.82);
            self.repeatInterval = self.timer(win.setTimeout(tick, speed));
          }
          tick();
        }, QTY_REPEAT_DELAY)
      );
    }

    stopRepeat() {
      if (this.repeatTimer) win.clearTimeout(this.repeatTimer);
      if (this.repeatInterval) win.clearTimeout(this.repeatInterval);
      this.repeatTimer = null;
      this.repeatInterval = null;
    }

    nudge(direction) {
      if (!this.input) return;
      var current = toInt(this.input.value, this.min);
      this.setValue(current + direction * this.step);
    }

    setValue(value) {
      if (!this.input) return;
      var next = clamp(value, this.min, this.max);
      if (String(next) === String(this.input.value)) {
        this.paintButtons(next);
        return;
      }
      this.input.value = String(next);
      this.paintButtons(next);
      emit(this.input, 'change', { value: next });
      emit(this, 'quantity:changed', { value: next });
    }

    clampValue(announceChange) {
      if (!this.input) return;
      var raw = this.input.value;
      var value = toInt(raw, this.min);
      var next = clamp(value, this.min, this.max);
      if (String(next) !== String(raw)) {
        this.input.value = String(next);
        if (announceChange) emit(this.input, 'change', { value: next });
      }
      this.paintButtons(next);
    }

    paintButtons(value) {
      if (this.minus) {
        var atMin = value <= this.min;
        this.minus.disabled = atMin;
        this.minus.setAttribute('aria-disabled', atMin ? 'true' : 'false');
      }
      if (this.plus) {
        var atMax = value >= this.max;
        this.plus.disabled = atMax;
        this.plus.setAttribute('aria-disabled', atMax ? 'true' : 'false');
      }
    }
  }

  define('quantity-input', QuantityInput);

  /* =======================================================================
     11 — <modal-dialog> and <modal-opener>
     A dialog that traps focus, closes on Escape and on the overlay, locks the
     page behind it and hands focus back to whatever opened it.

     <modal-opener data-modal="#SizeGuide"><button>…</button></modal-opener>
     <modal-dialog id="SizeGuide">
       <div data-modal-overlay></div>
       <div data-modal-panel role="document">
         <button data-modal-close>…</button>
       </div>
     </modal-dialog>
     ======================================================================= */

  class ModalDialog extends ZElement {
    connectedCallback() {
      this.panel = qs('[data-modal-panel]', this) || this.firstElementChild;
      this.overlay = qs('[data-modal-overlay]', this);
      this.opener = null;
      this.isOpen = this.getAttribute('data-state') === 'open';

      if (!this.hasAttribute('role')) this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('data-state')) this.setAttribute('data-state', 'closed');
      if (!this.isOpen) this.setAttribute('inert', '');

      var self = this;
      qsa('[data-modal-close]', this).forEach(function (button) {
        self.listen(button, 'click', function (event) {
          event.preventDefault();
          self.hide();
        });
      });

      if (this.overlay) {
        this.listen(this.overlay, 'click', function () {
          self.hide();
        });
      }

      this.listen(this, 'click', function (event) {
        // A click on the host itself, outside the panel, is a backdrop click.
        if (event.target === self) self.hide();
      });

      this.listen(doc, 'keydown', this.onKeydown.bind(this), true);
    }

    disconnectedCallback() {
      if (this.isOpen) this.hide(true);
      this.teardown();
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }

    show(opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = opener || (doc.activeElement !== doc.body ? doc.activeElement : null);

      this.removeAttribute('inert');
      this.setAttribute('data-state', 'open');
      if (doc.body) doc.body.setAttribute('data-modal-open', 'true');

      ZUtils.lockScroll();
      var initial =
        qs('[data-modal-initial]', this) ||
        qs('[data-modal-close]', this) ||
        ZUtils.getFocusable(this.panel || this)[0] ||
        this.panel ||
        this;
      ZUtils.trapFocus(this.panel || this, initial);

      emit(doc, 'zyrex:overlay', { open: true, source: 'modal' });
      emit(this, 'modal:opened', {});
    }

    hide(immediate) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.setAttribute('data-state', 'closed');
      if (doc.body) doc.body.removeAttribute('data-modal-open');

      ZUtils.unlockScroll();
      ZUtils.removeTrapFocus(this.opener);
      this.opener = null;

      var self = this;
      var delay = immediate || ZUtils.prefersReducedMotion() ? 0 : 320;
      this.timer(
        win.setTimeout(function () {
          if (!self.isOpen) self.setAttribute('inert', '');
        }, delay)
      );

      emit(doc, 'zyrex:overlay', { open: false, source: 'modal' });
      emit(this, 'modal:closed', {});
    }

    toggle(opener) {
      if (this.isOpen) this.hide();
      else this.show(opener);
    }
  }

  define('modal-dialog', ModalDialog);

  class ModalOpener extends ZElement {
    connectedCallback() {
      this.button = qs('button, a', this) || this;
      this.selector = this.getAttribute('data-modal') || this.getAttribute('data-modal-target');

      if (this.button && this.button !== this) {
        var target = this.modal();
        if (target && target.id && !this.button.getAttribute('aria-controls')) {
          this.button.setAttribute('aria-controls', target.id);
        }
        if (this.button.tagName === 'BUTTON' && !this.button.getAttribute('type')) {
          this.button.type = 'button';
        }
      }

      this.listen(this, 'click', this.onClick.bind(this));
    }

    disconnectedCallback() {
      this.teardown();
    }

    modal() {
      if (!this.selector) return null;
      var selector = this.selector.charAt(0) === '#' ? this.selector : '#' + this.selector;
      return qs(selector);
    }

    onClick(event) {
      var trigger = event.target && event.target.closest ? event.target.closest('button, a') : null;
      if (!trigger || !this.contains(trigger)) return;
      var target = this.modal();
      if (!target || !isFn(target.show)) return;
      event.preventDefault();
      target.show(trigger);
    }
  }

  define('modal-opener', ModalOpener);

  /* =======================================================================
     12 — <share-button>
     The native share sheet where the device has one, a clipboard copy where
     it does not. Either way the result is announced, because a silent copy is
     indistinguishable from a broken button.
     ======================================================================= */

  class ShareButton extends ZElement {
    connectedCallback() {
      this.button = qs('[data-share-button]', this) || qs('button', this);
      if (!this.button) return;

      this.urlInput = qs('[data-share-url]', this);
      this.message = qs('[data-share-message]', this);
      this.resetTimer = null;

      if (this.button.tagName === 'BUTTON' && !this.button.getAttribute('type')) {
        this.button.type = 'button';
      }
      if (this.message) this.message.setAttribute('aria-live', 'polite');

      this.listen(this.button, 'click', this.onClick.bind(this));
    }

    disconnectedCallback() {
      this.teardown();
    }

    shareUrl() {
      if (this.urlInput && this.urlInput.value) return this.urlInput.value;
      var attr = this.getAttribute('data-url') || this.getAttribute('data-share-link');
      return attr || win.location.href;
    }

    shareTitle() {
      return (
        this.getAttribute('data-title') ||
        (doc.title || (boot().shop && boot().shop.name) || '')
      );
    }

    onClick(event) {
      event.preventDefault();
      var url = this.shareUrl();
      var self = this;

      if (win.navigator && isFn(win.navigator.share) && ZUtils.isTouch()) {
        try {
          win.navigator
            .share({ title: this.shareTitle(), url: url })
            .catch(function () {
              /* the visitor dismissed the sheet — not an error */
            });
          return;
        } catch (error) {
          /* fall through to the clipboard */
        }
      }

      ZUtils.copyToClipboard(url).then(function (ok) {
        if (!ok) {
          ZToast.show({
            title: string('errors', 'title', 'Something went wrong'),
            text: string('errors', 'generic', 'Please try again.'),
            variant: 'error'
          });
          return;
        }
        self.confirm();
      });
    }

    confirm() {
      var copied = string('a11y', 'linkCopied', 'Link copied');
      ZUtils.announce(copied);
      this.setAttribute('data-state', 'copied');

      if (this.message) this.message.textContent = copied;
      else ZToast.show({ title: copied, variant: 'success', duration: 2600 });

      var self = this;
      if (this.resetTimer) win.clearTimeout(this.resetTimer);
      this.resetTimer = this.timer(
        win.setTimeout(function () {
          self.removeAttribute('data-state');
          if (self.message) self.message.textContent = '';
        }, 2600)
      );
    }
  }

  define('share-button', ShareButton);

  /* =======================================================================
     13 — <localization-form>
     Country and language pickers. Choosing a value writes it into the hidden
     field and submits, so the form works with a <select> and with a list of
     buttons, and still works with no JavaScript at all when the markup keeps
     its submit button.
     ======================================================================= */

  class LocalizationForm extends ZElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;

      this.input = qs('input[name="country_code"], input[name="locale_code"], [data-loc-input]', this.form);
      this.select = qs('select[data-loc-select], select[name="country_code"], select[name="locale_code"]', this.form);

      var self = this;

      if (this.select) {
        this.listen(this.select, 'change', function () {
          self.submit();
        });
        var submitButton = qs('[type="submit"]', this.form);
        if (submitButton) submitButton.classList.add('visually-hidden');
      }

      this.listen(this, 'click', function (event) {
        var option = event.target && event.target.closest ? event.target.closest('[data-value]') : null;
        if (!option || !self.contains(option)) return;
        event.preventDefault();
        self.choose(option);
      });
    }

    disconnectedCallback() {
      this.teardown();
    }

    choose(option) {
      var value = option.getAttribute('data-value');
      if (!value || !this.input) return;

      qsa('[data-value]', this).forEach(function (node) {
        var active = node === option;
        node.setAttribute('aria-selected', active ? 'true' : 'false');
        node.classList.toggle('is-active', active);
      });

      this.input.value = value;
      this.submit();
    }

    submit() {
      if (!this.form) return;
      try {
        if (isFn(this.form.requestSubmit)) this.form.requestSubmit();
        else this.form.submit();
      } catch (error) {
        this.form.submit();
      }
    }
  }

  define('localization-form', LocalizationForm);

  /* =======================================================================
     14 — <scroll-top>
     Appears once the page has scrolled a screen or so, returns to the top and
     — importantly — puts focus back at the start of the document, so a
     keyboard visitor is not left tabbing from the middle of the page.
     ======================================================================= */

  class ScrollTop extends ZElement {
    connectedCallback() {
      this.button = qs('[data-scroll-top-button]', this) || qs('button', this);
      this.threshold = toInt(this.getAttribute('data-threshold'), 600);
      this.visible = null;

      this.onScroll = ZUtils.rafThrottle(this.update.bind(this));
      this.listen(win, 'scroll', this.onScroll, { passive: true });

      if (this.button) {
        if (this.button.tagName === 'BUTTON' && !this.button.getAttribute('type')) {
          this.button.type = 'button';
        }
        this.listen(this.button, 'click', this.onClick.bind(this));
      }

      this.update();
    }

    disconnectedCallback() {
      this.teardown();
      if (this.onScroll && isFn(this.onScroll.cancel)) this.onScroll.cancel();
    }

    update() {
      var shown = (win.pageYOffset || root.scrollTop || 0) > this.threshold;
      if (shown === this.visible) return;
      this.visible = shown;
      this.classList.toggle('is-visible', shown);
      this.setAttribute('data-visible', shown ? 'true' : 'false');
      // No tabindex juggling: the host is `visibility: hidden` while it is
      // away, which already takes the button out of the tab order, and
      // forcing tabindex="-1" would strand it if any other rule reveals it.
    }

    onClick(event) {
      event.preventDefault();
      var behavior = ZUtils.prefersReducedMotion() ? 'auto' : 'smooth';
      try {
        win.scrollTo({ top: 0, behavior: behavior });
      } catch (error) {
        win.scrollTo(0, 0);
      }

      var target = qs('sticky-header a, .skip-link') || doc.body;
      focusSafely(target);
    }
  }

  define('scroll-top', ScrollTop);

  /* =======================================================================
     15 — <countdown-timer>
     Counts down to `data-end`. It never renders a negative number: once the
     deadline passes it clamps every part to zero, switches to the ended
     state and stops its own interval.

     Children, all optional:
       [data-countdown-days] [data-countdown-hours]
       [data-countdown-minutes] [data-countdown-seconds]
       [data-countdown-live]  shown while running
       [data-countdown-ended] shown once finished
     ======================================================================= */

  class CountdownTimer extends ZElement {
    connectedCallback() {
      this.parts = {
        days: qs('[data-countdown-days]', this),
        hours: qs('[data-countdown-hours]', this),
        minutes: qs('[data-countdown-minutes]', this),
        seconds: qs('[data-countdown-seconds]', this)
      };
      this.liveBlock = qs('[data-countdown-live]', this);
      this.endedBlock = qs('[data-countdown-ended]', this);

      this.endsAt = this.parseEnd();
      this.interval = null;
      this.finished = false;

      // The digits change every second, so nothing here is a live region:
      // announcing each tick would make the page unusable with a screen
      // reader. The surrounding copy carries the meaning.
      if (!this.endsAt) {
        this.finish(true);
        return;
      }

      this.tick();
      this.start();

      var self = this;
      this.listen(doc, 'visibilitychange', function () {
        if (doc.hidden) self.stop();
        else {
          self.tick();
          self.start();
        }
      });
    }

    disconnectedCallback() {
      this.stop();
      this.teardown();
    }

    parseEnd() {
      var raw = this.getAttribute('data-end') || this.getAttribute('data-countdown-end');
      if (!raw) return 0;

      var numeric = Number(raw);
      if (isFinite(numeric) && numeric > 100000) {
        // Seconds or milliseconds since the epoch.
        return numeric < 1e12 ? numeric * 1000 : numeric;
      }

      var parsed = Date.parse(raw);
      if (isFinite(parsed)) return parsed;

      // "2026-01-31 18:00" is not ISO — make it so before giving up.
      parsed = Date.parse(String(raw).replace(' ', 'T'));
      return isFinite(parsed) ? parsed : 0;
    }

    start() {
      if (this.interval || this.finished) return;
      var self = this;
      this.interval = this.timer(
        win.setInterval(function () {
          self.tick();
        }, 1000)
      );
    }

    stop() {
      if (!this.interval) return;
      win.clearInterval(this.interval);
      this.interval = null;
    }

    tick() {
      var remaining = this.endsAt - Date.now();

      if (remaining <= 0) {
        this.paint(0, 0, 0, 0);
        this.finish(false);
        return;
      }

      var totalSeconds = Math.floor(remaining / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      this.paint(days, hours, minutes, seconds);
      this.setAttribute('data-state', 'running');
    }

    paint(days, hours, minutes, seconds) {
      // Math.max is belt and braces: no part may ever show a minus sign.
      this.write(this.parts.days, Math.max(0, days), false);
      this.write(this.parts.hours, Math.max(0, hours), true);
      this.write(this.parts.minutes, Math.max(0, minutes), true);
      this.write(this.parts.seconds, Math.max(0, seconds), true);
    }

    write(node, value, pad) {
      if (!node) return;
      var text = pad && value < 10 ? '0' + value : String(value);
      if (node.textContent !== text) node.textContent = text;
    }

    finish(immediate) {
      if (this.finished) return;
      this.finished = true;
      this.stop();
      this.setAttribute('data-state', 'ended');

      if (this.liveBlock) this.liveBlock.setAttribute('hidden', 'hidden');
      if (this.endedBlock) this.endedBlock.removeAttribute('hidden');
      if (!this.endedBlock && attrOn(this, 'data-hide-on-end')) {
        this.setAttribute('hidden', 'hidden');
      }

      if (!immediate) emit(this, 'countdown:ended', {});
    }
  }

  define('countdown-timer', CountdownTimer);

  /* =======================================================================
     16 — <toast-stack>
     The host element. It owns nothing but registration: ZToast does the work,
     component-toast.css does the looks.
     ======================================================================= */

  class ToastStack extends ZElement {
    connectedCallback() {
      if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
      if (!this.hasAttribute('aria-atomic')) this.setAttribute('aria-atomic', 'false');
      ZToast.register(this);
    }

    disconnectedCallback() {
      ZToast.unregister(this);
    }
  }

  define('toast-stack', ToastStack);

  /* =======================================================================
     17 — Global enhancements
     Three behaviours that belong to the document rather than to any one
     element. All of them are delegated, so content added later — a rendered
     section, a drawer, a search panel — is covered without re-binding.
     ======================================================================= */

  /* ---- Anchor scrolling --------------------------------------------------
     base.css already sets `scroll-padding-top`, which handles the browser's
     own jump. This handles the scripted case, keeps the easing consistent
     with the rest of the theme, and — the part CSS cannot do — moves focus to
     the destination so the keyboard follows the eye. */

  function sameDocumentTarget(link) {
    if (!link || !link.getAttribute) return null;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '#' || href.length < 2) return null;
    if (link.getAttribute('target') === '_blank') return null;
    if (link.hasAttribute('data-no-anchor-scroll')) return null;

    var id = href.slice(1);
    var target = null;
    try {
      // Only a legacy <a name> counts as a fallback target: matching any
      // [name] would let `#quantity` resolve to a form field.
      target = doc.getElementById(id) || qs('a[name="' + id.replace(/"/g, '\\"') + '"]');
    } catch (error) {
      target = null;
    }
    return target;
  }

  function onDocumentClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    var target = sameDocumentTarget(link);
    if (!target) return;

    event.preventDefault();
    scrollToElement(target, 24);

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      on(target, 'blur', function once() {
        off(target, 'blur', once);
        target.removeAttribute('tabindex');
      });
    }
    focusSafely(target);

    var hash = link.getAttribute('href');
    try {
      if (win.history && isFn(win.history.pushState)) win.history.pushState(null, '', hash);
      else win.location.hash = hash;
    } catch (error) {
      /* a blocked history write must not stop the scroll */
    }
  }

  /** A hash in the address bar on first load lands under the sticky header. */
  function correctInitialHash() {
    if (!win.location.hash || win.location.hash.length < 2) return;
    var target = null;
    try {
      target = doc.getElementById(win.location.hash.slice(1));
    } catch (error) {
      target = null;
    }
    if (!target) return;
    win.setTimeout(function () {
      scrollToElement(target, 24);
    }, 90);
  }

  /* ---- External links ----------------------------------------------------
     Two jobs: `rel` hygiene on anything that opens a new tab, and a spoken
     hint so a link that leaves the page is not a surprise. The hint is only
     added when a translated string exists or the page is in English — an
     English aside inside a French storefront helps nobody. */

  function externalHint() {
    var strings = boot().strings || {};
    var a11y = strings.a11y || {};
    if (typeof a11y.newWindow === 'string' && a11y.newWindow.length) return a11y.newWindow;
    var lang = (root.getAttribute('lang') || '').toLowerCase();
    if (lang.indexOf('en') === 0) return '(opens in a new window)';
    return '';
  }

  function enhanceLinks(scope) {
    var host = scope && scope.querySelectorAll ? scope : doc;
    var hint = externalHint();

    qsa('a[href]', host).forEach(function (link) {
      if (link.hasAttribute('data-z-link')) return;
      link.setAttribute('data-z-link', '');

      var href = link.getAttribute('href') || '';
      if (!/^https?:/i.test(href)) return;

      var external = false;
      try {
        external = new URL(href, win.location.href).host !== win.location.host;
      } catch (error) {
        external = false;
      }
      if (!external) return;

      link.setAttribute('data-external', 'true');

      var rel = (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      if (link.getAttribute('target') === '_blank' && rel.indexOf('noreferrer') === -1) {
        rel.push('noreferrer');
      }
      link.setAttribute('rel', rel.join(' '));

      if (link.getAttribute('target') !== '_blank') return;
      if (!hint) return;
      if (link.getAttribute('aria-label') || link.getAttribute('title')) return;
      if (qs('[data-external-hint]', link)) return;

      var note = doc.createElement('span');
      note.className = 'visually-hidden';
      note.setAttribute('data-external-hint', '');
      note.textContent = ' ' + hint;
      link.appendChild(note);
    });
  }

  /* ---- Escape ------------------------------------------------------------
     When nothing owns the screen, Escape clears the newest message. When an
     overlay is open, Escape belongs to that overlay and this stays out of it. */

  function onGlobalKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (overlayOpen()) return;
    if (!ZToast.visible.length) return;
    var newest = ZToast.visible[ZToast.visible.length - 1];
    ZToast.dismiss(newest);
  }

  /* =======================================================================
     18 — Theme Editor
     Custom elements upgrade themselves when they are inserted, so most of
     this is measurement and state: re-publish the header height, re-scan for
     new links, and take the merchant straight to the block they just picked.
     ======================================================================= */

  function refreshHeader() {
    qsa('sticky-header').forEach(function (header) {
      if (isFn(header.measure)) header.measure();
      if (isFn(header.update)) header.update();
    });
  }

  function refreshSliders(scope) {
    qsa('slider-component', scope && scope.querySelectorAll ? scope : doc).forEach(function (slider) {
      if (isFn(slider.applyPerView)) slider.applyPerView();
      if (isFn(slider.buildDots)) slider.buildDots();
      if (isFn(slider.sync)) slider.sync();
    });
  }

  /** A stale lock would leave the page unscrollable after an editor edit. */
  function releaseStaleLock() {
    if (!doc.body || !doc.body.classList.contains('no-scroll')) return;
    if (qs('cart-drawer[data-state="open"], modal-dialog[data-state="open"]')) return;
    if (doc.body.hasAttribute('data-cart-drawer-open')) return;
    if (doc.body.hasAttribute('data-modal-open')) return;
    if (doc.body.hasAttribute('data-nav-open')) return;
    ZUtils.unlockScroll(true);
  }

  function onSectionLoad(event) {
    var section = event && event.target ? event.target : doc;
    enhanceLinks(section);
    refreshSliders(section);
    refreshHeader();
    releaseStaleLock();
  }

  function onSectionUnload() {
    refreshHeader();
    releaseStaleLock();
  }

  /** Reveal whatever the merchant selected: a panel, a tab or a slide. */
  function onBlockSelect(event) {
    var block = event && event.target ? event.target : null;
    if (!block || !block.closest) return;

    var accordion = block.closest('accordion-group');
    if (accordion && isFn(accordion.setOpen)) {
      var trigger = qs('[data-accordion-trigger], .accordion__trigger', block);
      if (trigger) {
        if (accordion.single && Array.isArray(accordion.triggers)) {
          accordion.triggers.forEach(function (other) {
            if (other !== trigger) accordion.setOpen(other, false);
          });
        }
        accordion.setOpen(trigger, true);
      }
    }

    var tabs = block.closest('tabs-group');
    if (tabs && Array.isArray(tabs.tabs) && isFn(tabs.select)) {
      var tab = tabs.tabs.indexOf(block) !== -1 ? block : qs('[role="tab"], .tab', block);
      var index = tabs.tabs.indexOf(tab);
      if (index !== -1) tabs.select(index, false);
    }

    var slider = block.closest('slider-component');
    if (slider && Array.isArray(slider.slides)) {
      if (isFn(slider.pauseAutoplay)) slider.pauseAutoplay();
      var slide = block.matches && block.matches('.slider__slide, [data-slider-slide]')
        ? block
        : block.closest('.slider__slide, [data-slider-slide]');
      var slideIndex = slider.slides.indexOf(slide);
      if (slideIndex !== -1 && isFn(slider.scrollToIndex)) slider.scrollToIndex(slideIndex);
    }
  }

  function onBlockDeselect(event) {
    var block = event && event.target ? event.target : null;
    if (!block || !block.closest) return;
    var slider = block.closest('slider-component');
    if (slider && isFn(slider.resumeAutoplay)) slider.resumeAutoplay();
  }

  function onSectionSelect(event) {
    var section = event && event.target ? event.target : null;
    qsa('slider-component', section || doc).forEach(function (slider) {
      if (isFn(slider.pauseAutoplay)) slider.pauseAutoplay();
    });
    refreshHeader();
  }

  function onSectionDeselect(event) {
    var section = event && event.target ? event.target : null;
    qsa('slider-component', section || doc).forEach(function (slider) {
      if (isFn(slider.resumeAutoplay)) slider.resumeAutoplay();
    });
  }

  /* =======================================================================
     19 — Boot
     ======================================================================= */

  on(doc, 'click', onDocumentClick);
  on(doc, 'keydown', onGlobalKeydown);

  on(doc, 'shopify:section:load', onSectionLoad);
  on(doc, 'shopify:section:unload', onSectionUnload);
  on(doc, 'shopify:section:select', onSectionSelect);
  on(doc, 'shopify:section:deselect', onSectionDeselect);
  on(doc, 'shopify:block:select', onBlockSelect);
  on(doc, 'shopify:block:deselect', onBlockDeselect);

  ZUtils.onReady(function () {
    root.classList.add('js-theme');
    enhanceLinks(doc);
    correctInitialHash();
    refreshHeader();

    if (ZUtils.isTouch()) root.classList.add('is-touch');

    // The stack may have been rendered before this file ran; adopt it now so
    // an early toast from cart.js has somewhere to land.
    var stack = qs('toast-stack, .toast-stack');
    if (stack) ZToast.register(stack);
  });
})();
